/**
 * SafeArea (PRD §7.1, C16, C21, C26): avoid-element registry, auto-detection, collision and
 * density checks. Geometry is pure; DOM scanning is a thin layer on top so both can be tested.
 */

/** @typedef {{left:number, top:number, right:number, bottom:number}} Rect */
/** @typedef {{rect:Rect, isAd:boolean, kind:string, el?:Element}} Obstacle */

/** Ad elements: GPT slots, AdSense units, Google ad iframes. */
export const AD_SELECTOR =
  'ins.adsbygoogle,[id^="div-gpt-ad"],iframe[id^="google_ads_iframe"],[data-google-query-id],.qsvp-ad-shell';
/** Google anchors, vignettes and out-of-page slots (§7.1 auto-detect). */
export const GOOGLE_OVERLAY_SELECTOR = 'ins.adsbygoogle[data-anchor-status],[data-vignette-loaded],[id^="gpt_unit_"]';
/** Fixed clickable elements the float must not cover. */
const CLICKABLE_SELECTOR = 'a,button,input,select,textarea,[role="button"]';

/**
 * @param {number} left @param {number} top @param {number} width @param {number} height
 * @returns {Rect}
 */
export function makeRect(left, top, width, height) {
  return { left, top, right: left + width, bottom: top + height };
}

/**
 * @param {Rect} rect
 * @param {number} gap
 * @returns {Rect}
 */
export function inflate(rect, gap) {
  return { left: rect.left - gap, top: rect.top - gap, right: rect.right + gap, bottom: rect.bottom + gap };
}

/**
 * @param {Rect} a @param {Rect} b
 * @returns {boolean} true when the rectangles overlap with positive area
 */
export function intersects(a, b) {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/**
 * @param {Rect} rect
 * @returns {number}
 */
export function area(rect) {
  return Math.max(0, rect.right - rect.left) * Math.max(0, rect.bottom - rect.top);
}

/**
 * @param {Rect} a @param {Rect} b
 * @returns {Rect}
 */
export function clip(a, b) {
  return {
    left: Math.max(a.left, b.left),
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
  };
}

/**
 * C16: float + every sticky ad as a share of the screen.
 * 2.0.5 (A47): a sticky ad inside another counted sticky (a sidebar ad in a sticky rail) is counted once, by
 * its outer element.
 * @param {object} input
 * @param {Rect} input.viewport
 * @param {Rect} input.floatRect
 * @param {Obstacle[]} input.obstacles
 * @returns {number} 0..1
 */
export function stickyCoverage({ viewport, floatRect, obstacles }) {
  const viewportArea = area(viewport);
  if (!viewportArea) return 1;
  let covered = area(clip(floatRect, viewport));
  for (const obstacle of obstacles) {
    // An element containing an ad is itself an ad (isAdElement), so any containing obstacle already counts it.
    // DOM obstacles always carry el; rect-only ones (no el) are never treated as nested.
    const nested = obstacles.some((outer) => outer !== obstacle && outer.el && outer.el.contains(obstacle.el));
    if (obstacle.isAd && !nested) covered += area(clip(obstacle.rect, viewport));
  }
  return covered / viewportArea;
}

/**
 * Candidate rectangle for a dock spot (§7, §7.1 step 2).
 * Bottom spots sit 8 px from the edge and 8 px above every pinned element in their column;
 * top-right sits 8 px below them.
 * @param {object} input
 * @param {string} input.dock 'bottom-right'|'bottom-left'|'top-right'|'gutter'
 * @param {[number, number]} input.size float size
 * @param {Rect} input.viewport
 * @param {Obstacle[]} input.obstacles
 * @param {number} input.gap 8
 * @param {Rect|null} input.column article column rect (gutter docking)
 * @returns {Rect|null}
 */
export function dockRect({ dock, size, viewport, obstacles, gap, column }) {
  const [width, height] = size;
  let left;
  if (dock === 'gutter') {
    if (!column) return null;
    const gutterWidth = viewport.right - column.right;
    if (gutterWidth < width + 2 * gap) return null;
    left = viewport.right - gap - width;
  } else if (dock === 'bottom-left') {
    left = viewport.left + gap;
  } else {
    left = viewport.right - gap - width;
  }
  const right = left + width;
  const overlapsColumn = (rect) => rect.left < right + gap && left - gap < rect.right;
  // Only pinned elements (stickies, banners, fixed controls) move the spot; inline ads scroll past and are
  // collisions only, or the float would jump every time an ad touched the screen edge.
  const pinned = obstacles.filter((obstacle) => obstacle.kind !== 'inline-ad');
  // 2.0.5 (A46): the float stacks clear of every pinned element in its column (back-to-top, chat buttons),
  // not only those touching the screen edge. Moves only go one way, so each element is passed at most once;
  // a spot pushed off screen is skipped by findDock().
  const top = dock === 'top-right';
  let y = top ? viewport.top + gap : viewport.bottom - gap - height;
  for (let moved = 1; moved; ) {
    moved = 0;
    for (const { rect } of pinned) {
      if (overlapsColumn(rect) && rect.top < y + height + gap && y - gap < rect.bottom) {
        y = top ? rect.bottom + gap : rect.top - gap - height;
        moved = 1;
      }
    }
  }
  return makeRect(left, y, width, height);
}

/**
 * Find the first dock spot that fits on screen (§7.1 step 2). Float + sticky ads over 30% of the screen
 * (C16) blocks the float (2.0.2, A42). Other obstacles do not block it (A37); pinned stickies only move the spot.
 * @param {object} input
 * @param {[number, number]} input.size float size
 * @param {Rect} input.viewport
 * @param {Obstacle[]} input.obstacles avoid elements, detected stickies, ads in view, fixed clickables
 * @param {string[]} input.dockOrder config.floatDock
 * @param {string} input.profileName
 * @param {Rect|null} input.column article column rect
 * @param {number} [input.gap]
 * @param {number} [input.maxCoverage]
 * @returns {{dock:string, rect:Rect, coverage:number}|{dock:null, reason:string, coverage?:number}}
 */
export function findDock({ size, viewport, obstacles, dockOrder, profileName, column, gap = 8, maxCoverage = 0.3 }) {
  const order = [];
  // §7 / §7.1 step 3: desktop docks in the right gutter first when it is at least 416 px wide.
  if (profileName === 'desktop' && column && viewport.right - column.right >= size[0] + 2 * gap) order.push('gutter');
  for (const dock of dockOrder) {
    if (dock === 'top-right' && profileName !== 'desktop') continue; // top-right: desktop only
    order.push(dock);
  }
  // C16 / C21 (2.0.2, A42): float + sticky ads must stay at or under 30% of the screen.
  const coverage = stickyCoverage({ viewport, floatRect: makeRect(0, 0, size[0], size[1]), obstacles });
  if (coverage > maxCoverage) return { dock: null, reason: 'density', coverage };
  for (const dock of order) {
    const rect = dockRect({ dock, size, viewport, obstacles, gap, column });
    if (!rect) continue;
    if (rect.top < viewport.top || rect.left < viewport.left || rect.right > viewport.right || rect.bottom > viewport.bottom) {
      continue;
    }
    return { dock: dock === 'gutter' ? 'bottom-right' : dock, rect, coverage, gutter: dock === 'gutter' };
  }
  return { dock: null, reason: 'collision', coverage };
}

/**
 * Element rect as a plain Rect.
 * @param {Element} el
 * @returns {Rect}
 */
function rectOf(el) {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

/**
 * True when the element renders something visible.
 * @param {Element} el
 * @param {Window} win
 * @returns {boolean}
 */
function isRendered(el, win) {
  const style = win.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * True when an element is an ad or contains one.
 * @param {Element} el
 * @returns {boolean}
 */
export function isAdElement(el) {
  return !!(el.matches && (el.matches(AD_SELECTOR) || el.querySelector(AD_SELECTOR)));
}

/**
 * Create the DOM-side SafeArea registry for a page (§7.1 step 1).
 * @param {object} options
 * @param {Window} options.win
 * @param {Document} options.doc
 * @param {string[]} options.avoidSelectors
 * @param {Function} [options.ResizeObserverCtor]
 * @param {Function} [options.onChange] obstacles changed size (anchor expand/collapse)
 * @returns {object}
 */
export function createSafeArea({ win, doc, avoidSelectors, ResizeObserverCtor, MutationObserverCtor, onChange = () => {} }) {
  let detected = []; // cached auto-detected fixed/sticky elements
  const watched = new Set();
  const resizeObserver = ResizeObserverCtor ? new ResizeObserverCtor(() => onChange('resize')) : null;
  // 2.0.18: sites show a filled sticky by switching visibility (same size), which a ResizeObserver never reports.
  const attrObserver = MutationObserverCtor ? new MutationObserverCtor(() => onChange('attributes')) : null;

  /**
   * Watch an element's size and visibility so anchors that change creative height, and stickies that appear when
   * they fill, re-trigger the check.
   * @param {Element} el
   */
  function watch(el) {
    if (watched.has(el)) return;
    watched.add(el);
    if (resizeObserver) resizeObserver.observe(el);
    if (attrObserver) attrObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
  }

  return {
    /**
     * Rescan for fixed/sticky elements we do not own (on each float attempt, and on DOM changes).
     * @param {Element[]} ours elements to ignore (our own floating player)
     */
    rescan(ours = []) {
      const found = [];
      for (const el of doc.body.querySelectorAll('*')) {
        if (ours.some((own) => own === el || own.contains(el) || el.contains(own))) continue;
        if (el.closest('[data-qsvp-debug]')) continue; // the QA debug panel is not page furniture
        const position = win.getComputedStyle(el).position;
        if (position === 'fixed' || position === 'sticky') found.push(el);
      }
      for (const el of doc.querySelectorAll(GOOGLE_OVERLAY_SELECTOR)) if (!found.includes(el)) found.push(el);
      detected = found;
      found.forEach(watch);
      return found.length;
    },

    /**
     * Current obstacles with live rectangles (§7.1 step 2 test set).
     * @param {Element[]} ours elements to ignore
     * @returns {Obstacle[]}
     */
    obstacles(ours = []) {
      const isOurs = (el) => ours.some((own) => own === el || own.contains(el) || el.contains(own));
      const viewport = makeRect(0, 0, win.innerWidth, win.innerHeight);
      const list = [];
      const seen = new Set();
      const add = (el, kind) => {
        // Each element counts once, or a sticky ad would be double-counted in the 30% coverage (C16).
        if (!el || seen.has(el) || isOurs(el)) return;
        // 2.0.18 (replaces the 2.0.1 hand edit): a registered sticky hidden while it waits for fill is no longer an
        // obstacle, so the float docks at the screen edge instead of above an empty gap. When it fills and shows,
        // watch() reports it and the float re-check moves the float above it.
        const rect = rectOf(el);
        const shown = isRendered(el, win);
        if (!shown) return;
        seen.add(el);
        if (!intersects(rect, viewport)) return;
        // Only sticky (pinned) ads count toward the C16 30% screen coverage. 2.0.5 (A48): a registered sticky
        // that is hidden while empty keeps the float above it but shows no ad, so it does not count.
        list.push({ el, rect, kind, isAd: kind !== 'inline-ad' && shown && isAdElement(el) });
      };
      for (const selector of avoidSelectors) {
        for (const el of doc.querySelectorAll(selector)) {
          watch(el);
          const position = win.getComputedStyle(el).position;
          // Registered stickies are obstacles only while they are pinned to the screen.
          if (position === 'fixed' || position === 'sticky') add(el, 'registered');
        }
      }
      for (const el of detected) add(el, 'detected');
      // Every GPT slot and AdSense unit in the viewport, fixed or not (§7.1 step 2).
      for (const el of doc.querySelectorAll(AD_SELECTOR)) add(el, 'inline-ad');
      // Fixed clickable elements.
      for (const el of doc.querySelectorAll(CLICKABLE_SELECTOR)) {
        const position = win.getComputedStyle(el).position;
        if (position === 'fixed') add(el, 'control');
      }
      return list;
    },

    /**
     * 2.0.11 (stickyMakeRoom): the float is blocked by the 30% sticky coverage (C16). Destroy the fewest pinned GPT
     * sticky ads in view (Google anchors first: the float replaces the top sticky, FL-3) that bring the float plus the
     * remaining stickies to 30% or less, and collapse their emptied containers. A slot is destroyed, never hidden
     * while live. Nothing is removed when the float would not fit even without every removable sticky.
     * Call right after a float attempt, so the obstacle scan is fresh.
     * @param {[number, number]} size float box (frame + header + border)
     * @param {Element[]} ours
     * @param {boolean} [anchorsOnly] 2.0.16: only Google anchor ads (gpt_unit_*), for the poster float
     * @returns {string[]} ad unit paths removed (empty: nothing removed)
     */
    makeRoom(size, ours, anchorsOnly) {
      const gt = win.googletag;
      if (!gt || !gt.pubads || !gt.destroySlots) return [];
      const slots = gt.pubads().getSlots();
      const slotsIn = (el) =>
        slots.filter((slot) => {
          const node = doc.getElementById(slot.getSlotElementId());
          return node && el.contains(node);
        });
      const obstacles = this.obstacles(ours);
      const removable = obstacles
        .filter((obstacle) => obstacle.isAd && obstacle.kind !== 'inline-ad' && slotsIn(obstacle.el).length)
        .filter((obstacle) => !anchorsOnly || /^gpt_unit_/.test(obstacle.el.id))
        .sort((a, b) => (b.kind === 'detected') - (a.kind === 'detected'));
      const viewport = makeRect(0, 0, win.innerWidth, win.innerHeight);
      const floatRect = makeRect(0, 0, size[0], size[1]);
      const picked = [];
      let rest = obstacles;
      while (stickyCoverage({ viewport, floatRect, obstacles: rest }) > 0.3) {
        const next = removable[picked.length];
        if (!next) return [];
        picked.push(next);
        rest = rest.filter((obstacle) => obstacle !== next);
      }
      const paths = [];
      for (const { el } of picked) {
        const gone = slotsIn(el);
        gone.forEach((slot) => paths.push(slot.getAdUnitPath()));
        gt.destroySlots(gone);
        el.style.setProperty('display', 'none', 'important');
      }
      return paths;
    },

    /**
     * §7.1: interstitial or vignette on screen.
     * @returns {boolean}
     */
    isFullscreenAdOpen() {
      for (const el of doc.querySelectorAll('[data-vignette-loaded="true"],ins.adsbygoogle[data-vignette-loaded]')) {
        if (isRendered(el, win)) return true;
      }
      const viewportArea = win.innerWidth * win.innerHeight;
      for (const el of detected) {
        if (!isRendered(el, win) || !isAdElement(el)) continue;
        if (area(clip(rectOf(el), makeRect(0, 0, win.innerWidth, win.innerHeight))) > viewportArea * 0.8) return true;
      }
      return false;
    },

    /** Stop observing (teardown). */
    disconnect() {
      if (resizeObserver) resizeObserver.disconnect();
      watched.clear();
    },
  };
}

/**
 * C21: mobile ad density, all ads in the main content as a share of its height.
 * @param {object} input
 * @param {number} input.contentHeight main content height
 * @param {number[]} input.adHeights heights of inline ads, stickies, player slots and fallbacks
 * @returns {number}
 */
export function mobileAdDensity({ contentHeight, adHeights }) {
  if (!contentHeight) return 1;
  return adHeights.reduce((sum, height) => sum + height, 0) / contentHeight;
}
